import { useTranslation } from "react-i18next";
/**
 * MediaSearchResultItem — Single media result row in the command palette.
 *
 * Renders a compact media item with thumbnail, title/subtitle, type badge,
 * and optional quality badges (4K, HDR).  Follows the same visual rhythm
 * and ARIA patterns as the page-result rows in AppSearch.
 */

import { forwardRef, useState } from "react";
import { Icon } from "../ui/Icon";
import { Tooltip } from "../ui/Tooltip.js";
import { ScrollingText } from "../ui/ScrollingText.js";
import type { MediaSearchResult } from "../../hooks/useMediaSearch.js";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface MediaSearchResultItemProps {
  item: MediaSearchResult;
  isSelected: boolean;
  index: number;
  onSelect: () => void;
  onHover: () => void;
}

// ---------------------------------------------------------------------------
// Type badge styling
// ---------------------------------------------------------------------------

const TYPE_BADGE_CLASSES: Record<string, string> = {
  Movie: "bg-blue-500/10 text-blue-600",
  Series: "bg-purple-500/10 text-purple-600",
  Episode: "bg-amber-500/10 text-amber-600",
};
const DEFAULT_BADGE_CLASS = "bg-muted/40 text-muted-foreground";

// ---------------------------------------------------------------------------
// Fallback icon per media type
// ---------------------------------------------------------------------------

function getFallbackIcon(type: string): string {
  if (type === "Movie") return "lucide:film";
  if (type === "Series") return "lucide:tv";
  return "lucide:play-circle";
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const MediaSearchResultItem = forwardRef<HTMLButtonElement, MediaSearchResultItemProps>(
  function MediaSearchResultItem({ item, isSelected, index, onSelect, onHover }, ref) {
    const { t } = useTranslation("shell");
    const [imgError, setImgError] = useState(false);
    const badgeClass = TYPE_BADGE_CLASSES[item.type] ?? DEFAULT_BADGE_CLASS;

    return (
      <button
        ref={ref}
        id={`app-search-item-${index}`}
        role="option"
        aria-selected={isSelected}
        type="button"
        onClick={onSelect}
        onMouseEnter={onHover}
        className={`flex w-full items-center gap-3 px-3 py-2 text-left text-sm transition-colors ${
          isSelected
            ? "bg-primary/10 text-foreground"
            : "text-foreground hover:bg-muted/60"
        }`}
      >
        {/* Thumbnail (32x48 portrait poster) */}
        <div className="h-8 w-6 flex-shrink-0 overflow-hidden rounded-sm bg-muted/30">
          {item.imageTag && !imgError ? (
            <img
              src={`/api/streaming/thumbnail/${item.id}?server=${item.serverType}&type=Primary&tag=${item.imageTag}&maxWidth=80`}
              alt=""
              className="h-full w-full object-cover"
              loading="lazy"
              onError={() => setImgError(true)}
            />
          ) : (
            <Icon
              icon={getFallbackIcon(item.type)}
              className="h-full w-full p-1 text-muted-foreground"
            />
          )}
        </div>

        {/* Title + subtitle */}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <Tooltip content={item.name} side="top" delay={400} className="min-w-0">
              <ScrollingText text={item.name} className="text-sm" />
            </Tooltip>
            {item.productionYear != null && (
              <span className="flex-shrink-0 text-xs text-muted-foreground">
                ({item.productionYear})
              </span>
            )}
          </div>

          {/* Episode subtitle: series name + S##E## */}
          {item.type === "Episode" && item.seriesName && (() => {
            const subtitle = `${item.seriesName}${item.seasonNumber != null ? ` S${String(item.seasonNumber).padStart(2, "0")}` : ""}${item.episodeNumber != null ? `E${String(item.episodeNumber).padStart(2, "0")}` : ""}`;
            return (
              <Tooltip content={subtitle} side="bottom" delay={400} className="max-w-full">
                <ScrollingText text={subtitle} className="text-xs text-muted-foreground !font-normal" />
              </Tooltip>
            );
          })()}

          {/* Non-episode subtitle: genres */}
          {item.type !== "Episode" && item.genres.length > 0 && (
            <Tooltip content={item.genres.join(", ")} side="bottom" delay={300}>
              <div className="hidden truncate text-xs text-muted-foreground sm:block">
                {item.genres.slice(0, 3).join(", ")}
              </div>
            </Tooltip>
          )}
        </div>

        {/* Type badge */}
        <span
          className={`flex-shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium ${badgeClass}`}
        >
          {item.type === "Movie" ? t($ => $.movie) : item.type === "Series" ? t($ => $.series) : item.type === "Episode" ? t($ => $.episode) : item.type}
        </span>

        {/* Quality badges */}
        {item.quality === "4K" && (
          <span className="flex-shrink-0 rounded border border-amber-500/30 bg-amber-500/15 px-1 py-0.5 text-[10px] font-semibold text-amber-600">
            4K
          </span>
        )}
        {item.isHdr && (
          <span className="flex-shrink-0 rounded border border-violet-500/30 bg-violet-500/15 px-1 py-0.5 text-[10px] font-semibold text-violet-600">
            HDR
          </span>
        )}

        {/* Enter hint on selected */}
        {isSelected && (
          <span className="flex-shrink-0 text-xs text-muted-foreground">
            &#x21B5;
          </span>
        )}
      </button>
    );
  }
);
